import { RIYAL } from "@/lib/currency";
import { useMemo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useProperties } from "@/hooks/useProperties";
import { useUnits } from "@/hooks/useUnits";
import { useTenants, Tenant } from "@/hooks/useTenants";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { UnitsBreakdownTable } from "../UnitsBreakdownTable";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Pencil, Building2, CreditCard, Settings2, Zap, Droplets, Flame, Wifi, Wrench } from "lucide-react";
import { formatNumber } from "@/lib/formatters";
import { isRentVatApplicable, deriveTenantPaidServicesTotal, diffDurationInclusive, computeDownPayment, saleScheduledBalance } from "@/lib/contractHelpers";
import { format, addMonths } from "date-fns";
import {
  ContractWizardFormData,
  CONTRACT_TYPES,
  DURATION_UNITS,
  PAYMENT_METHODS,
  PAYERS,
  FEE_TYPES,
  WizardStep,
  PaymentScheduleItem,
  FeeConfig,
} from "./types";

const VAT_RATE = 15;

interface ReviewStepProps {
  formData: ContractWizardFormData;
  onGoToStep: (step: WizardStep) => void;
  isEditMode?: boolean;
  originalData?: ContractWizardFormData;
}

export function ReviewStep({ formData, onGoToStep, isEditMode, originalData }: ReviewStepProps) {
  const { t, language } = useLanguage();

  const { data: properties = [] } = useProperties();
  const { data: units = [] } = useUnits(formData.propertyId || undefined);
  const { data: tenants = [] } = useTenants();

  // Sale flags — drive down-payment display, the delivery-date relabel, hiding
  // the (rent-only) Services card, and the balance-based schedule preview.
  const isSale = formData.contractType === "sale";
  const isImmediateSale = isSale && formData.saleType === "immediate";
  const isOffplanSale = isSale && formData.saleType === "offplan";
  const saleDownPayment = isSale
    ? computeDownPayment(Number(formData.totalAmount) || 0, Number(formData.depositAmount) || 0, formData.downPaymentType)
    : 0;

  const selectedProperty = properties.find((p) => p.id === formData.propertyId);
  const selectedUnit = units.find((u) => u.id === formData.unitId);
  const selectedTenant = tenants.find((t) => t.id === formData.tenantId);

  const contractType = CONTRACT_TYPES.find((c) => c.value === formData.contractType);
  const durationUnit = DURATION_UNITS.find((d) => d.value === formData.durationUnit);
  const paymentMethod = PAYMENT_METHODS.find((m) => m.value === formData.preferredPaymentMethod);

  // Prefer the exact Ejar-style Y/M/D breakdown derived from the actual
  // start/end dates (accurate for both create and edit — the legacy single-
  // unit durationValue/durationUnit pair is an approximation kept only for
  // the downstream schedule generator). Falls back to the legacy display
  // when a date isn't set yet (shouldn't happen by step 4, but defensive).
  const durationBreakdown = diffDurationInclusive(formData.startDate, formData.endDate);
  const hasDurationBreakdown =
    !!formData.startDate &&
    !!formData.endDate &&
    (durationBreakdown.years > 0 || durationBreakdown.months > 0 || durationBreakdown.days > 0);
  const durationDisplay = hasDurationBreakdown
    ? [
        durationBreakdown.years > 0 ? `${durationBreakdown.years} ${t("سنوات", "Years")}` : null,
        durationBreakdown.months > 0 ? `${durationBreakdown.months} ${t("شهور", "Months")}` : null,
        durationBreakdown.days > 0 ? `${durationBreakdown.days} ${t("أيام", "Days")}` : null,
      ]
        .filter(Boolean)
        .join(" ")
    : `${formData.durationValue} ${durationUnit ? t(durationUnit.label, durationUnit.labelEn) : ""}`;

  // VAT rule: residential leases are exempt; everything else is taxed at 15%.
  // We classify by the UNIT's purpose, not the property's — a multi_purpose
  // building can host residential apartments AND commercial offices, and
  // ZATCA applies VAT per lease (per unit). Fall back to property.purpose
  // when the unit hasn't been picked yet so the preview isn't empty during
  // the wizard's earlier steps.
  const purpose = selectedUnit?.purpose ?? selectedProperty?.purpose;
  const propertyType = selectedProperty?.property_type;
  const isCommercial = isRentVatApplicable(purpose);
  const rentVatRate = isCommercial ? VAT_RATE : 0;

  const getTenantName = (tenant: Tenant) => {
    if (tenant.tenant_type === "commercial") {
      return language === "ar"
        ? tenant.commercial_name || ""
        : tenant.commercial_name_en || tenant.commercial_name || "";
    }
    const arName = `${tenant.first_name || ""} ${tenant.last_name || ""}`.trim();
    const enName = `${tenant.first_name_en || ""} ${tenant.last_name_en || ""}`.trim();
    return language === "ar" ? arName : enName || arName;
  };

  const formatDisplayDate = (dateStr: string) => {
    if (!dateStr) return "-";
    return format(new Date(dateStr), "d/M/yyyy");
  };

  const getPayerLabel = (payer: string) => {
    const found = PAYERS.find((p) => p.value === payer);
    return found ? t(found.label, found.labelEn) : payer;
  };

  const formatFeeDisplay = (
    fee: FeeConfig,
    totalAmount: string,
    t: (ar: string, en: string) => string,
    getPayerLabel: (payer: string) => string
  ) => {
    const amount =
      fee.type === "percentage"
        ? (Number(totalAmount) * Number(fee.value)) / 100
        : Number(fee.value);
    const vat = amount * 0.15;
    const total = amount + vat;
    const typeLabel = fee.type === "percentage" ? `${fee.value}%` : t("مبلغ ثابت", "Fixed");
    return `${formatNumber(amount)}\u00A0${RIYAL} (${typeLabel}) + ${formatNumber(vat)} ${t("ضريبة", "VAT")} = ${formatNumber(total)}\u00A0${RIYAL} - ${getPayerLabel(fee.payer)}`;
  };

  const getUtilityTypeLabel = (type: string) => {
    const labels: Record<string, { ar: string; en: string }> = {
      meter: { ar: "عداد", en: "Meter" },
      fixed: { ar: "مبلغ ثابت", en: "Fixed" },
      included: { ar: "مشمول", en: "Included" },
      not_available: { ar: "غير متوفر", en: "N/A" },
    };
    return t(labels[type]?.ar || type, labels[type]?.en || type);
  };

  // Generate payment schedule for preview
  const paymentSchedule = useMemo((): PaymentScheduleItem[] => {
    // SALE: the schedule bills the balance (sale − down payment); an immediate
    // sale is a single payment at the sale date. RENT/SUBLEASE: full amount.
    const saleBalance = saleScheduledBalance(Number(formData.totalAmount) || 0, saleDownPayment);
    const count = isImmediateSale ? 1 : Number(formData.paymentsCount) || 0;
    const total = isSale ? saleBalance : Number(formData.totalAmount) || 0;
    const firstDate = isImmediateSale
      ? formData.firstPaymentDate || formData.startDate
      : formData.firstPaymentDate;

    // Calculate contract duration in months
    let contractMonths = 12;
    const durationValue = Number(formData.durationValue) || 12;
    switch (formData.durationUnit) {
      case "day":
        contractMonths = Math.max(1, Math.floor(durationValue / 30));
        break;
      case "year":
        contractMonths = durationValue * 12;
        break;
      case "month":
      default:
        contractMonths = durationValue;
        break;
    }

    if (count <= 0 || total <= 0 || !firstDate) return [];

    const paymentAmount = total / count;
    const monthsBetweenPayments = Math.floor(contractMonths / count);
    const schedule: PaymentScheduleItem[] = [];

    for (let i = 0; i < count; i++) {
      const dueDate = addMonths(new Date(firstDate), i * monthsBetweenPayments);
      schedule.push({
        paymentNumber: i + 1,
        dueDate: format(dueDate, "yyyy-MM-dd"),
        amount: Math.round(paymentAmount * 100) / 100,
      });
    }

    return schedule;
  }, [formData.paymentsCount, formData.totalAmount, formData.firstPaymentDate, formData.startDate, formData.durationValue, formData.durationUnit, isSale, isImmediateSale, saleDownPayment]);

  // VAT totals for payment summary
  const rentTotal = Number(formData.totalAmount) || 0;
  const rentVatAmount = Math.round(rentTotal * rentVatRate) / 100;
  // AC4 — tenant-paid annual service fees fold into the rent invoices (no VAT)
  // and so must be counted in the contract grand total. Owner-paid services are
  // EJAR-record-only and never invoiced, so they're excluded — matching the
  // create RPC fold and the repository's authoritative total derivation.
  //
  // ROUNDING FIX: use deriveTenantPaidServicesTotal with the SAME paymentsCount
  // that the persistence path uses (paymentSchedule.length == paymentsCount).
  // Raw annual summing diverges from the persisted total by a few halalas because
  // the RPC rounds per-payment (ROUND(annual/n, 2) × n) before summing.
  // Using the same helper here makes displayed == persisted == Σ(invoice subtotals).
  const paymentsCount = Number(formData.paymentsCount) || 0;
  // Map wizard form rows to the UnitServiceFeesFold shape the helper expects.
  const unitsFoldShape = formData.units.map((u) => ({
    service_fees: Object.fromEntries(
      Object.entries(u.serviceFees).map(([key, entry]) => [
        key,
        { amount: entry.amount, payer: entry.payer },
      ])
    ),
  }));
  const tenantServicesTotal = deriveTenantPaidServicesTotal(unitsFoldShape, paymentsCount);
  const contractFeeAmount = Number(formData.contractFee) || 0;
  const managementFeeAmount = Number(formData.managementFee) || 0;
  const totalFees = contractFeeAmount + managementFeeAmount;
  const feesVatAmount = Math.round(totalFees * VAT_RATE) / 100;
  // Services are VAT-exempt (folded into rent subtotal, not taxed); add them to
  // the grand total without VAT.
  const grandTotal =
    rentTotal + rentVatAmount + tenantServicesTotal + totalFees + feesVatAmount;

  const SectionHeader = ({
    title,
    icon: Icon,
    step,
  }: {
    title: string;
    icon: React.ComponentType<{ className?: string }>;
    step: WizardStep;
  }) => (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <span className="font-semibold">{title}</span>
      </div>
      <Button variant="ghost" size="sm" onClick={() => onGoToStep(step)} className="gap-1 text-xs h-7">
        <Pencil className="h-3 w-3" />
        {t("تعديل", "Edit")}
      </Button>
    </div>
  );

  const InfoRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="flex justify-between py-1.5 border-b last:border-0">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span className="font-medium text-sm">{value}</span>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Contract Information */}
      <Card>
        <CardHeader className="py-3 px-4">
          <SectionHeader title={t("معلومات العقد", "Contract Information")} icon={Building2} step="info" />
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0">
          <InfoRow
            label={t("نوع العقد", "Contract Type")}
            value={<Badge variant="outline">{contractType ? t(contractType.label, contractType.labelEn) : "-"}</Badge>}
          />
          <InfoRow
            label={t("العقار", "Property")}
            value={
              <span className="flex items-center gap-2">
                {selectedProperty ? (language === "ar" ? selectedProperty.name : selectedProperty.name_en || selectedProperty.name) : "-"}
                {selectedProperty && (
                  <Badge variant={isCommercial ? "secondary" : "outline"} className="text-xs">
                    {isCommercial
                      ? t("تجاري", "Commercial")
                      : propertyType === "residential"
                      ? t("سكني", "Residential")
                      : propertyType === "industrial"
                      ? t("صناعي", "Industrial")
                      : propertyType === "land"
                      ? t("أرض", "Land")
                      : propertyType === "mixed"
                      ? t("مختلط", "Mixed")
                      : t("غير محدد", "Unspecified")}
                  </Badge>
                )}
              </span>
            }
          />
          {/* Single-unit case still shows the legacy "Unit" line. Multi-unit
              breakdown is rendered as its own table below the basic info
              card so it has room for rent + 4 service fee columns. */}
          {formData.units.filter((r) => r.unitId).length <= 1 ? (
            <InfoRow label={t("الوحدة", "Unit")} value={selectedUnit?.unit_number || "-"} />
          ) : (
            <InfoRow
              label={t("الوحدات", "Units")}
              value={`${formData.units.filter((r) => r.unitId).length} ${t("وحدات", "units")}`}
            />
          )}
          <InfoRow
            label={formData.contractType === "sale" ? t("المشتري", "Buyer") : t("المستأجر", "Tenant")}
            value={selectedTenant ? getTenantName(selectedTenant) : "-"}
          />
          <InfoRow label={isSale ? t("تاريخ البيع", "Sale Date") : t("تاريخ البداية", "Start Date")} value={formatDisplayDate(formData.startDate)} />
          {/* Immediate sales have no term — hide duration + end date. Off-plan
              relabels the end date as the delivery date. */}
          {!isImmediateSale && <InfoRow label={isOffplanSale ? t("مدة التسليم", "Delivery Term") : t("مدة العقد", "Duration")} value={durationDisplay} />}
          {!isImmediateSale && (
            <InfoRow
              label={isOffplanSale ? t("تاريخ التسليم", "Delivery Date") : t("تاريخ النهاية", "End Date")}
              value={formatDisplayDate(formData.endDate)}
            />
          )}
          <InfoRow
            label={formData.contractType === "sale" ? t("مبلغ البيع", "Sale Amount") : t("مبلغ الإيجار", "Rent Amount")}
            value={
              <span className="flex items-center gap-2">
                {`${formatNumber(Number(formData.totalAmount) || 0)}\u00A0${RIYAL}`}
                {formData.contractType !== "sale" && (
                  <Badge variant={isCommercial ? "secondary" : "outline"} className="text-xs">
                    {isCommercial
                      ? t(`ض.ق.م ${VAT_RATE}%`, `VAT ${VAT_RATE}%`)
                      : t("معفي من الضريبة", "VAT Exempt")}
                  </Badge>
                )}
              </span>
            }
          />
          {/* Sale → resolved down payment (fixed SAR or % of sale), deducted from
              the total. Rent/sublease → security deposit as entered. */}
          {isSale
            ? saleDownPayment > 0 && (
                <InfoRow
                  label={t("الدفعة الأولى", "Down Payment")}
                  value={`${formatNumber(saleDownPayment)}\u00A0${RIYAL}${
                    formData.downPaymentType === "percentage" ? ` (${formData.depositAmount}%)` : ""
                  }`}
                />
              )
            : formData.depositAmount && (
                <InfoRow label={t("مبلغ التأمين", "Deposit")} value={`${formatNumber(Number(formData.depositAmount))}\u00A0${RIYAL}`} />
              )}
          {formData.brokerageFee?.value && (
            <InfoRow
              label={t("رسوم السعي", "Brokerage Fee")}
              value={formatFeeDisplay(formData.brokerageFee, formData.totalAmount, t, getPayerLabel)}
            />
          )}
          {formData.marketingFee?.value && (
            <InfoRow
              label={t("رسوم التسويق", "Marketing Fee")}
              value={formatFeeDisplay(formData.marketingFee, formData.totalAmount, t, getPayerLabel)}
            />
          )}
          {formData.contractManagementFee?.value && (
            <InfoRow
              label={t("رسوم إدارة العقود", "Contract Mgmt Fee")}
              value={formatFeeDisplay(formData.contractManagementFee, formData.totalAmount, t, getPayerLabel)}
            />
          )}
          {!isSale && formData.propertyManagementFee?.value && (
            <InfoRow
              label={t("رسوم إدارة الأملاك", "Property Mgmt Fee")}
              value={formatFeeDisplay(formData.propertyManagementFee, formData.totalAmount, t, getPayerLabel)}
            />
          )}
        </CardContent>
      </Card>

      {/* Multi-unit breakdown — renders only when the wizard has more than
          one unit row filled. Single-unit contracts skip this card so the
          review stays compact. Uses the same <UnitsBreakdownTable> as the
          post-save contract details view — single visual + summation source. */}
      {formData.units.filter((r) => r.unitId).length > 1 && (
        <Card>
          <CardHeader className="py-3 px-4">
            <SectionHeader
              title={t("تفاصيل الوحدات", "Unit breakdown")}
              icon={Building2}
              step="info"
            />
          </CardHeader>
          <CardContent className="px-4 pb-4 pt-0">
            <UnitsBreakdownTable
              rows={formData.units
                .filter((r) => r.unitId)
                .map((row, idx) => {
                  const unit = units.find((u) => u.id === row.unitId);
                  // Map the form-row's per-service { amount: string, payer }
                  // shape into the table's display shape (numeric amount +
                  // optional payer for the tenant/owner marker).
                  const serviceFees = Object.fromEntries(
                    Object.entries(row.serviceFees).map(([key, entry]) => [
                      key,
                      entry.amount && Number(entry.amount) > 0
                        ? { amount: Number(entry.amount), payer: entry.payer }
                        : null,
                    ])
                  );
                  return {
                    key: `${row.unitId}-${idx}`,
                    unitLabel: unit?.unit_number ?? row.unitId,
                    rentAmount: row.rentAmount ? Number(row.rentAmount) : 0,
                    serviceFees,
                  };
                })}
            />
          </CardContent>
        </Card>
      )}

      {/* Payment Information */}
      <Card>
        <CardHeader className="py-3 px-4">
          <SectionHeader title={t("معلومات الدفع", "Payment Information")} icon={CreditCard} step={isImmediateSale ? "info" : "payment"} />
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0">
          <InfoRow label={t("عدد الدفعات", "Payments")} value={isImmediateSale ? "1" : formData.paymentsCount} />
          <InfoRow
            label={t("تاريخ أول دفعة", "First Payment")}
            value={formatDisplayDate(
              isImmediateSale ? formData.firstPaymentDate || formData.startDate : formData.firstPaymentDate,
            )}
          />
          <InfoRow
            label={t("طريقة الدفع", "Payment Method")}
            value={paymentMethod ? t(paymentMethod.label, paymentMethod.labelEn) : t("غير محدد", "Not specified")}
          />

          {/* Payment Schedule Table */}
          {paymentSchedule.length > 0 && (
            <div className="mt-3 border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="w-16 text-center py-2">#</TableHead>
                    <TableHead className="py-2">{t("تاريخ الاستحقاق", "Due Date")}</TableHead>
                    <TableHead className="text-end py-2">{t("المبلغ قبل الضريبة", "Subtotal")}</TableHead>
                    {formData.contractType !== "sale" && (
                      <TableHead className="text-end py-2">
                        {isCommercial
                          ? t(`ض.ق.م ${VAT_RATE}%`, `VAT ${VAT_RATE}%`)
                          : t("الضريبة", "VAT")}
                      </TableHead>
                    )}
                    <TableHead className="text-end py-2">{t("الإجمالي", "Total")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paymentSchedule.map((payment) => {
                    // Rent/sublease add the security deposit onto payment #1 (a
                    // cosmetic preview). A sale's down payment is DEDUCTED from the
                    // schedule, not added — so no "+ Deposit" row for sales.
                    const deposit = isSale ? 0 : Number(formData.depositAmount) || 0;
                    const isFirstWithDeposit = payment.paymentNumber === 1 && deposit > 0;
                    const displayAmount = isFirstWithDeposit ? payment.amount + deposit : payment.amount;
                    const vatOnPayment = Math.round(displayAmount * rentVatRate) / 100;
                    const paymentTotal = displayAmount + vatOnPayment;

                    return (
                      <TableRow key={payment.paymentNumber}>
                        <TableCell className="text-center py-2">
                          {payment.paymentNumber}
                          {isFirstWithDeposit && (
                            <Badge variant="outline" className="ms-2 text-xs">
                              {t("+ تأمين", "+ Deposit")}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="py-2">{formatDisplayDate(payment.dueDate)}</TableCell>
                        <TableCell className="text-end py-2">
                          {formatNumber(displayAmount)}&nbsp;{RIYAL}
                          {isFirstWithDeposit && (
                            <div className="text-xs text-muted-foreground">
                              {t(
                                `إيجار ${formatNumber(payment.amount)} + تأمين ${formatNumber(deposit)}`,
                                `Rent ${formatNumber(payment.amount)} + Deposit ${formatNumber(deposit)}`
                              )}
                            </div>
                          )}
                        </TableCell>
                        {formData.contractType !== "sale" && (
                          <TableCell className="text-end py-2">
                            {isCommercial ? (
                              <span className="text-muted-foreground">{formatNumber(vatOnPayment)}&nbsp;{RIYAL}</span>
                            ) : (
                              <Badge variant="outline" className="text-xs font-normal">
                                {t("معفي", "Exempt")}
                              </Badge>
                            )}
                          </TableCell>
                        )}
                        <TableCell className="text-end py-2 font-medium">
                          {formatNumber(paymentTotal)}&nbsp;{RIYAL}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* VAT Summary */}
          {formData.contractType !== "sale" && rentTotal > 0 && (
            <div className="mt-3 pt-3 border-t space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{t("إجمالي الإيجار (قبل الضريبة)", "Total Rent (excl. VAT)")}</span>
                <span>{formatNumber(rentTotal)}&nbsp;{RIYAL}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {isCommercial
                    ? t(`ضريبة القيمة المضافة على الإيجار (${VAT_RATE}%)`, `VAT on Rent (${VAT_RATE}%)`)
                    : t("ضريبة القيمة المضافة على الإيجار", "VAT on Rent")}
                </span>
                {isCommercial ? (
                  <span>{formatNumber(rentVatAmount)}&nbsp;{RIYAL}</span>
                ) : (
                  <Badge variant="outline" className="text-xs font-normal">
                    {t("معفي من الضريبة", "VAT Exempt")}
                  </Badge>
                )}
              </div>
              {tenantServicesTotal > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {t("الخدمات (معفاة من الضريبة)", "Services (VAT exempt)")}
                  </span>
                  <span>{formatNumber(tenantServicesTotal)}&nbsp;{RIYAL}</span>
                </div>
              )}
              {totalFees > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t("إجمالي الرسوم (قبل الضريبة)", "Total Fees (excl. VAT)")}</span>
                    <span>{formatNumber(totalFees)}&nbsp;{RIYAL}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      {t(`ضريبة القيمة المضافة على الرسوم (${VAT_RATE}%)`, `VAT on Fees (${VAT_RATE}%)`)}
                    </span>
                    <span>{formatNumber(feesVatAmount)}&nbsp;{RIYAL}</span>
                  </div>
                </>
              )}
              <Separator className="my-1" />
              <div className="flex justify-between text-sm font-bold">
                <span>{t("الإجمالي الكلي (شامل الضريبة)", "Grand Total (incl. VAT)")}</span>
                <span className="text-primary">{formatNumber(grandTotal)}&nbsp;{RIYAL}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Services — sale contracts have no annual services / auto-renewal / notice
          (Services step is removed for sales), so the card is hidden entirely. */}
      {!isSale && (
      <Card>
        <CardHeader className="py-3 px-4">
          <SectionHeader title={t("الخدمات", "Services")} icon={Settings2} step="services" />
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0">
          {/* Utilities */}
          <div className="space-y-2 mb-3">
            <div className="flex items-center gap-2 text-sm">
              <Zap className="h-3.5 w-3.5 text-primary" />
              <span className="text-muted-foreground">{t("الكهرباء:", "Electricity:")}</span>
              <span className="font-medium">
                {getUtilityTypeLabel(formData.electricity.type)}
                {formData.electricity.type === "fixed" && formData.electricity.amount && ` - ${formatNumber(Number(formData.electricity.amount))}\u00A0${RIYAL}`}
                {formData.electricity.type !== "not_available" && ` (${getPayerLabel(formData.electricity.payer)})`}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Droplets className="h-3.5 w-3.5 text-primary" />
              <span className="text-muted-foreground">{t("الماء:", "Water:")}</span>
              <span className="font-medium">
                {getUtilityTypeLabel(formData.water.type)}
                {formData.water.type === "fixed" && formData.water.amount && ` - ${formatNumber(Number(formData.water.amount))}\u00A0${RIYAL}`}
                {formData.water.type !== "not_available" && ` (${getPayerLabel(formData.water.payer)})`}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Flame className="h-3.5 w-3.5 text-primary" />
              <span className="text-muted-foreground">{t("الغاز:", "Gas:")}</span>
              <span className="font-medium">
                {getUtilityTypeLabel(formData.gas.type)}
                {formData.gas.type === "fixed" && formData.gas.amount && ` - ${formatNumber(Number(formData.gas.amount))}\u00A0${RIYAL}`}
                {formData.gas.type !== "not_available" && ` (${getPayerLabel(formData.gas.payer)})`}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Wifi className="h-3.5 w-3.5 text-primary" />
              <span className="text-muted-foreground">{t("الإنترنت:", "Internet:")}</span>
              <span className="font-medium">
                {getUtilityTypeLabel(formData.internet.type)}
                {formData.internet.type === "fixed" && formData.internet.amount && ` - ${formatNumber(Number(formData.internet.amount))}\u00A0${RIYAL}`}
                {formData.internet.type !== "not_available" && ` (${getPayerLabel(formData.internet.payer)})`}
              </span>
            </div>
            {formData.maintenanceFee && (
              <div className="flex items-center gap-2 text-sm">
                <Wrench className="h-3.5 w-3.5 text-primary" />
                <span className="text-muted-foreground">{t("رسوم الصيانة:", "Maintenance:")}</span>
                <span className="font-medium">
                  {formatNumber(Number(formData.maintenanceFee))}&nbsp;{RIYAL} ({getPayerLabel(formData.maintenanceFeePayer)})
                </span>
              </div>
            )}
          </div>

          <InfoRow
            label={t("التجديد التلقائي", "Auto Renewal")}
            value={
              <Badge variant={formData.autoRenewal ? "default" : "secondary"}>
                {formData.autoRenewal ? t("مفعل", "Enabled") : t("معطل", "Disabled")}
              </Badge>
            }
          />
          <InfoRow label={t("فترة الإشعار", "Notice Period")} value={`${formData.noticePeriod || 30} ${t("يوم", "days")}`} />

          {formData.notes && (
            <div className="pt-2 border-t mt-2">
              <span className="text-xs text-muted-foreground">{t("ملاحظات إضافية", "Additional Notes")}</span>
              <p className="mt-1 text-sm">{formData.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
}
